import React, { createContext, useContext, useEffect, useState } from "react";

/*
 * Lightweight i18n: a language context (en/es) + a full string dictionary.
 * useT() returns the current-language tree; useLang() exposes lang/setLang.
 */

export const STRINGS = {
  en: {
    nav: { home: "Home", about: "About", markets: "Markets", partners: "Partners", hubs: "Hubs", contact: "Contact" },
    hubs: {
      heroKicker: "Hubs",
      heroTitle: "Three desks, one network.",
      heroBody: "NEXXIS operates from hubs across the Americas and Europe — keeping a local presence close to the relationships we serve, around the clock.",
      items: [
        { city: "Medellín", country: "Colombia", role: "LATAM HUB" },
        { city: "Madrid", country: "España", role: "EUROPE HUB" },
        { city: "Limassol", country: "Cyprus", role: "EMEA DESK" },
      ],
      cta: "Get directions",
      footTitle: "Operating across time zones.",
      footBody: "Wherever your markets are, a NEXXIS desk is awake to listen.",
      footLink: "Talk to a desk",
    },
    home: {
      heroKicker: "NEXXIS — LATAM · Global Markets",
      heroTitle: "LATAM's gateway to global markets.",
      heroBody:
        "We connect investors, traders, and partners across Latin America with the world's leading financial markets — through trusted relationships, local expertise, and seamless access.",
      heroCta1: "Discover NEXXIS",
      heroCta2: "Get in touch",
      whatKicker: "What we do",
      whatTitle: "A trusted bridge between Latin America and global finance.",
      whatWeDo: [
        { title: "Connect", body: "We bridge Latin American capital with the world's leading financial markets." },
        { title: "Empower", body: "We support traders, advisors, and partners across the region with global access." },
        { title: "Educate", body: "We share market context and knowledge so partners can make informed decisions." },
      ],
      pillarsKicker: "Our pillars",
      pillarsTitle: "Four principles that guide every relationship.",
      pillarsLede: "These pillars inform how we listen, advise, and act — with consistency and care.",
      pillars: [
        { title: "Access", body: "Trusted routes to world-class financial markets, opened with intention and oversight." },
        { title: "Trust", body: "Long-term relationships built on transparency, compliance, and clarity of intent." },
        { title: "Growth", body: "A measured expansion of opportunity informed by education, not urgency." },
        { title: "Connectivity", body: "A bridge between Latin America and the global financial ecosystem." },
      ],
      marketsKicker: "Markets we serve",
      marketsTitle: "Access, fanned out across asset classes.",
      marketsLede: "Drag, swipe, or use the dots — each card opens a window onto how we approach a market.",
      showcase: [
        { title: "Equities", description: "Ownership across developed and emerging venues." },
        { title: "Fixed Income", description: "Duration treated as a relationship, not a trade." },
        { title: "FX", description: "Currencies as stories — macro framing without theater." },
        { title: "Derivatives", description: "Instruments to express a thesis, not amplify it." },
        { title: "Global Reach", description: "Access to leading liquidity hubs across regions." },
      ],
      ctaTitle: "Building bridges, one conversation at a time.",
      ctaBody: "Whether you are a trader, advisor, or institutional partner, we are here to listen.",
      ctaLink: "Begin a conversation",
    },
    markets: {
      heroKicker: "Markets",
      heroTitle: "Markets we cover.",
      heroBody:
        "A clear map of how NEXXIS approaches market access. We'll publish coverage detail as relationships mature.",
      catKicker: "Categories",
      catTitle: "Four lenses, one posture.",
      categories: [
        { title: "Equities", body: "Listed companies and ownership stakes across developed and emerging venues.", bullets: ["Patient routing", "Counterparty diligence", "Editorial briefings"] },
        { title: "Fixed Income", body: "Duration treated as a relationship — curated counterparties and cadence.", bullets: ["Long-form thinking", "Plain-language framing", "Counterparty craft"] },
        { title: "FX", body: "Currencies as stories. Macro framing without macro theater.", bullets: ["Context first", "Access to serious flows", "Composure over noise"] },
        { title: "Derivatives", body: "Listed and bilateral instruments designed to express a thesis, not amplify it.", bullets: ["Intentional risk", "Curated venues", "Operational rigor"] },
      ],
      regKicker: "Regions",
      regTitle: "A network with global reach.",
      regions: [
        { name: "Americas", body: "Connecting LATAM to North American and regional venues." },
        { name: "EMEA", body: "Bridging Europe, the Middle East, and Africa." },
        { name: "Asia-Pacific", body: "Access to leading APAC liquidity hubs." },
      ],
      ctaLink: "See who we work with",
    },
    partners: {
      heroKicker: "Partners",
      heroTitle: "Who we work with.",
      heroBody:
        "NEXXIS partners with the professionals who carry the relationships, regulation, and rigor that markets deserve.",
      segments: [
        { title: "Introducing Brokers", body: "Regional IBs who bring depth of relationship and execution. We support coverage, onboarding, and global venue introductions.", notes: ["White-glove onboarding", "Cross-border introductions", "Operational continuity"] },
        { title: "Affiliates", body: "Editorial, educational, and analytical partners whose audiences are sophisticated.", notes: ["Editorial alignment", "Compliance-led messaging", "Long-term partnership"] },
        { title: "Wealth Advisors", body: "Independent advisors and RIAs serving HNW clients across the region.", notes: ["Suitability-aware", "Reporting alignment", "Discreet operations"] },
        { title: "Family Offices", body: "Single- and multi-family offices seeking governance-grade access and curated counterparty selection.", notes: ["Bespoke routing", "Counterparty curation", "Confidentiality"] },
        { title: "Institutional", body: "Funds, treasuries, and small institutions building cross-border programs.", notes: ["Process discipline", "Audit-ready trails", "Quiet execution"] },
      ],
      cardCta: "Partner with us",
      ctaLink: "Begin a partner conversation",
    },
    about: {
      heroKicker: "About NEXXIS",
      heroTitle: "Building bridges across Latin America.",
      heroBody: "NEXXIS was built on a simple belief — that geography should never be a barrier to opportunity.",
      mission: "Mission",
      missionText: "Bridge Latin America with the world's leading financial markets through trusted relationships, education, and market access.",
      vision: "Vision",
      visionText: "Become the leading market-access and brokerage-introduction brand serving Latin America.",
      storyKicker: "Our story",
      storyTitle: "A region with global ambition.",
      storyParas: [
        "NEXXIS was founded by professionals who saw a fragmented bridge between Latin American capital and the world's leading financial venues. Investors, traders, and partners across the region deserved a more thoughtful path forward.",
        "We built NEXXIS around long-form relationships — with introducing brokers, advisors, and institutional partners — grounded in transparency, compliance, and clarity of intent.",
        "Today, we continue to connect the region to global markets through trusted channels, with the patience and rigor that Latin America deserves.",
      ],
      valuesKicker: "Values",
      valuesTitle: "Worked, not worn.",
      valuesLede: "Our values inform how we listen, advise, and act — quietly and consistently.",
      values: [
        { title: "Clarity", body: "We translate complex global markets into clear, navigable terms." },
        { title: "Patience", body: "We move at the speed of trust. Relationships compound where transactions fade." },
        { title: "Stewardship", body: "We see our role as custodial. Markets are stewarded, not chased." },
      ],
      valueCta: "Explore our approach",
      flipHint: "FLIP TO READ",
    },
    contact: {
      heroKicker: "Contact",
      heroTitle: "Get in touch.",
      heroBody: "No urgency, no scripts. Tell us what you are exploring — we'll reply within two business days.",
      formKicker: "Write to us",
      name: "Name",
      email: "Email",
      message: "Message",
      respondNote: "We respond within two business days.",
      send: "Send message",
      sending: "Sending…",
      sent: "Sent",
      required: "Required",
      invalidEmail: "Invalid email",
      toastTitle: "Thank you. We will be in touch.",
      toastDesc: "NEXXIS replies within two business days.",
      infoKicker: "Direct",
      infoBody: "For partnership, advisory, and institutional inquiries.",
      emailLabel: "Email",
      replyLabel: "Reply time",
      replyValue: "Within two business days",
      coverageLabel: "Coverage",
      coverageValue: "Latin America and international",
      learnAbout: "Learn more about NEXXIS",
    },
    footer: {
      desc: "Connecting Latin America with the world's leading financial markets through trusted relationships, education, and access.",
      navigate: "Navigate",
      direct: "Direct",
      directNote: "Editorial & partner inquiries.",
      location: "Latin America · Global markets",
      response: "We reply within two business days",
      rights: "LATAM's Gateway to Global Markets",
      disc1: "Informational use only.",
      disc2: "No investment advice.",
    },
  },

  es: {
    nav: { home: "Inicio", about: "Nosotros", markets: "Mercados", partners: "Socios", hubs: "Sedes", contact: "Contacto" },
    hubs: {
      heroKicker: "Sedes",
      heroTitle: "Tres mesas, una red.",
      heroBody: "NEXXIS opera desde sedes en América y Europa — manteniendo presencia local cerca de las relaciones que atendemos, a toda hora.",
      items: [
        { city: "Medellín", country: "Colombia", role: "SEDE LATAM" },
        { city: "Madrid", country: "España", role: "SEDE EUROPA" },
        { city: "Limassol", country: "Chipre", role: "MESA EMEA" },
      ],
      cta: "Cómo llegar",
      footTitle: "Operando entre husos horarios.",
      footBody: "Donde sea que estén tus mercados, una mesa de NEXXIS está despierta para escuchar.",
      footLink: "Habla con una mesa",
    },
    home: {
      heroKicker: "NEXXIS — LATAM · Mercados Globales",
      heroTitle: "La puerta de LATAM a los mercados globales.",
      heroBody:
        "Conectamos a inversionistas, traders y socios de toda América Latina con los principales mercados financieros del mundo — a través de relaciones de confianza, experiencia local y acceso sin fricciones.",
      heroCta1: "Descubre NEXXIS",
      heroCta2: "Conversemos",
      whatKicker: "Qué hacemos",
      whatTitle: "Un puente confiable entre América Latina y las finanzas globales.",
      whatWeDo: [
        { title: "Conectar", body: "Unimos el capital latinoamericano con los principales mercados financieros del mundo." },
        { title: "Potenciar", body: "Acompañamos a traders, asesores y socios de la región con acceso global." },
        { title: "Educar", body: "Compartimos contexto y conocimiento de mercado para tomar decisiones informadas." },
      ],
      pillarsKicker: "Nuestros pilares",
      pillarsTitle: "Cuatro principios que guían cada relación.",
      pillarsLede: "Estos pilares definen cómo escuchamos, asesoramos y actuamos — con consistencia y cuidado.",
      pillars: [
        { title: "Acceso", body: "Rutas confiables a mercados financieros de clase mundial, abiertas con intención y supervisión." },
        { title: "Confianza", body: "Relaciones de largo plazo basadas en transparencia, cumplimiento y claridad de intención." },
        { title: "Crecimiento", body: "Una expansión medida de oportunidades guiada por la educación, no por la urgencia." },
        { title: "Conectividad", body: "Un puente entre América Latina y el ecosistema financiero global." },
      ],
      marketsKicker: "Mercados que cubrimos",
      marketsTitle: "Acceso, desplegado por clase de activo.",
      marketsLede: "Arrastra, desliza o usa los puntos — cada tarjeta abre una ventana a cómo abordamos un mercado.",
      showcase: [
        { title: "Renta Variable", description: "Participación en plazas desarrolladas y emergentes." },
        { title: "Renta Fija", description: "La duración tratada como una relación, no como una operación." },
        { title: "Divisas (FX)", description: "Las monedas como relatos — marco macro sin teatro." },
        { title: "Derivados", description: "Instrumentos para expresar una tesis, no para amplificarla." },
        { title: "Alcance Global", description: "Acceso a los principales centros de liquidez de cada región." },
      ],
      ctaTitle: "Construyendo puentes, una conversación a la vez.",
      ctaBody: "Ya seas trader, asesor o socio institucional, estamos aquí para escucharte.",
      ctaLink: "Iniciar una conversación",
    },
    markets: {
      heroKicker: "Mercados",
      heroTitle: "Mercados que cubrimos.",
      heroBody:
        "Un mapa claro de cómo NEXXIS aborda el acceso a mercados. Publicaremos el detalle de cobertura a medida que las relaciones maduren.",
      catKicker: "Categorías",
      catTitle: "Cuatro lentes, una misma postura.",
      categories: [
        { title: "Renta Variable", body: "Empresas listadas y participaciones de propiedad en plazas desarrolladas y emergentes.", bullets: ["Ruteo paciente", "Diligencia de contraparte", "Informes editoriales"] },
        { title: "Renta Fija", body: "La duración tratada como una relación — contrapartes y cadencia curadas.", bullets: ["Pensamiento de largo plazo", "Lenguaje claro", "Oficio de contraparte"] },
        { title: "Divisas (FX)", body: "Las monedas como relatos. Marco macro sin teatro macro.", bullets: ["Contexto primero", "Acceso a flujos serios", "Calma sobre el ruido"] },
        { title: "Derivados", body: "Instrumentos listados y bilaterales diseñados para expresar una tesis, no para amplificarla.", bullets: ["Riesgo intencional", "Plazas curadas", "Rigor operativo"] },
      ],
      regKicker: "Regiones",
      regTitle: "Una red con alcance global.",
      regions: [
        { name: "Américas", body: "Conectando LATAM con plazas de Norteamérica y la región." },
        { name: "EMEA", body: "Puente entre Europa, Medio Oriente y África." },
        { name: "Asia-Pacífico", body: "Acceso a los principales centros de liquidez de APAC." },
      ],
      ctaLink: "Ver con quién trabajamos",
    },
    partners: {
      heroKicker: "Socios",
      heroTitle: "Con quién trabajamos.",
      heroBody:
        "NEXXIS se asocia con los profesionales que cargan las relaciones, la regulación y el rigor que los mercados merecen.",
      segments: [
        { title: "Introducing Brokers", body: "IBs regionales que aportan profundidad de relación y ejecución. Apoyamos cobertura, onboarding e introducciones a plazas globales.", notes: ["Onboarding de guante blanco", "Introducciones transfronterizas", "Continuidad operativa"] },
        { title: "Afiliados", body: "Socios editoriales, educativos y analíticos cuyas audiencias son sofisticadas.", notes: ["Alineación editorial", "Mensajería con cumplimiento", "Alianza de largo plazo"] },
        { title: "Asesores Patrimoniales", body: "Asesores independientes y RIAs que atienden a clientes de alto patrimonio en la región.", notes: ["Conscientes de idoneidad", "Alineación de reportes", "Operación discreta"] },
        { title: "Family Offices", body: "Family offices uni y multifamiliares que buscan acceso con grado de gobernanza y selección curada de contrapartes.", notes: ["Ruteo a medida", "Curaduría de contrapartes", "Confidencialidad"] },
        { title: "Institucionales", body: "Fondos, tesorerías e instituciones pequeñas que construyen programas transfronterizos.", notes: ["Disciplina de procesos", "Trazas listas para auditoría", "Ejecución silenciosa"] },
      ],
      cardCta: "Asóciate con nosotros",
      ctaLink: "Iniciar una conversación de socios",
    },
    about: {
      heroKicker: "Sobre NEXXIS",
      heroTitle: "Construyendo puentes en América Latina.",
      heroBody: "NEXXIS nació de una creencia simple — que la geografía nunca debería ser una barrera para la oportunidad.",
      mission: "Misión",
      missionText: "Conectar a América Latina con los principales mercados financieros del mundo a través de relaciones de confianza, educación y acceso a mercados.",
      vision: "Visión",
      visionText: "Convertirnos en la marca líder de acceso a mercados e introducción de brokers al servicio de América Latina.",
      storyKicker: "Nuestra historia",
      storyTitle: "Una región con ambición global.",
      storyParas: [
        "NEXXIS fue fundada por profesionales que vieron un puente fragmentado entre el capital latinoamericano y las principales plazas financieras del mundo. Los inversionistas, traders y socios de la región merecían un camino más reflexivo.",
        "Construimos NEXXIS en torno a relaciones de largo plazo — con introducing brokers, asesores y socios institucionales — cimentadas en transparencia, cumplimiento y claridad de intención.",
        "Hoy seguimos conectando a la región con los mercados globales a través de canales confiables, con la paciencia y el rigor que América Latina merece.",
      ],
      valuesKicker: "Valores",
      valuesTitle: "De trabajo, no de adorno.",
      valuesLede: "Nuestros valores definen cómo escuchamos, asesoramos y actuamos — con discreción y consistencia.",
      values: [
        { title: "Claridad", body: "Traducimos mercados globales complejos en términos claros y navegables." },
        { title: "Paciencia", body: "Avanzamos a la velocidad de la confianza. Las relaciones componen donde las transacciones se desvanecen." },
        { title: "Custodia", body: "Vemos nuestro rol como custodial. Los mercados se cuidan, no se persiguen." },
      ],
      valueCta: "Conoce nuestro enfoque",
      flipHint: "VOLTEAR PARA LEER",
    },
    contact: {
      heroKicker: "Contacto",
      heroTitle: "Conversemos.",
      heroBody: "Sin urgencias, sin guiones. Cuéntanos qué estás explorando — respondemos en dos días hábiles.",
      formKicker: "Escríbenos",
      name: "Nombre",
      email: "Correo",
      message: "Mensaje",
      respondNote: "Respondemos en dos días hábiles.",
      send: "Enviar mensaje",
      sending: "Enviando…",
      sent: "Enviado",
      required: "Requerido",
      invalidEmail: "Correo inválido",
      toastTitle: "Gracias. Estaremos en contacto.",
      toastDesc: "NEXXIS responde en dos días hábiles.",
      infoKicker: "Directo",
      infoBody: "Para consultas de alianzas, asesoría e institucionales.",
      emailLabel: "Correo",
      replyLabel: "Tiempo de respuesta",
      replyValue: "En dos días hábiles",
      coverageLabel: "Cobertura",
      coverageValue: "América Latina e internacional",
      learnAbout: "Conoce más sobre NEXXIS",
    },
    footer: {
      desc: "Conectando América Latina con los principales mercados financieros del mundo a través de relaciones de confianza, educación y acceso.",
      navigate: "Navegar",
      direct: "Directo",
      directNote: "Consultas editoriales y de socios.",
      location: "América Latina · Mercados globales",
      response: "Respondemos en dos días hábiles",
      rights: "La puerta de LATAM a los mercados globales",
      disc1: "Solo con fines informativos.",
      disc2: "No es asesoría de inversión.",
    },
  },
};

const LangContext = createContext({ lang: "en", setLang: () => {}, toggle: () => {} });

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem("nexxis_lang");
      if (saved === "en" || saved === "es") return saved;
    } catch {}
    return "en";
  });

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    try {
      localStorage.setItem("nexxis_lang", lang);
    } catch {}
  }, [lang]);

  const toggle = () => setLang((l) => (l === "es" ? "en" : "es"));

  return <LangContext.Provider value={{ lang, setLang, toggle }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
export const useT = () => STRINGS[useContext(LangContext).lang];
